import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { wilmaSAVCustomerId } from "../../config";
import { useAuth } from "../../contexts/AuthContext";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCallCentreSettings from "../../data/useCallCentreSettings";
import useSettings from "../../data/useSettings";
import useWilmaUsers from "../../data/useWilmaUsers";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { CallLeadType, ICallAgent } from "../../models/CallQueue";
import { ISetting } from "../../models/Setting";
import { IWilmaUser } from "../../models/auth/WilmaUser";
import { LEAD_TYPE_LABELS } from "../../utils/callQueue";
import classNames from "../../utils/classNames";

interface Field {
  key: string;
  label: string;
  help?: string;
  type?: "text" | "number" | "time" | "date" | "textarea" | "toggle";
  savHidden?: boolean;
}

// The keys match CallCentreSettingsReader in the API.
const GROUPS: { title: string; description: string; fields: Field[] }[] = [
  {
    title: "Calling hours",
    description: "Leads are only served between these times, London time. Retries and call backs move inside them.",
    fields: [
      { key: "CallCentre:CallingHoursStart", label: "Start", type: "time" },
      { key: "CallCentre:CallingHoursEnd", label: "End", type: "time" },
    ],
  },
  {
    title: "Attempts and holds",
    description: "No answer and Voicemail left count as attempts. After the last one the lead goes to Manager Assistance.",
    fields: [
      { key: "CallCentre:MaxAttempts", label: "Attempts before a manager", type: "number" },
      { key: "CallCentre:RetryHours", label: "Hours until the first retry", help: "Later retries are the next morning.", type: "number" },
      { key: "CallCentre:ServeHoldMinutes", label: "Minutes a served lead is held", help: "If the agent leaves it, it goes back to the queue after this.", type: "number" },
      { key: "CallCentre:CallBackGraceMinutes", label: "Minutes an agent keeps their call back", help: "After it's due, before it joins the pool.", type: "number" },
      { key: "CallCentre:UnlockQueue", label: "Unlock the queue", help: "Let every agent open any lead from Up next, instead of being served them in turn. Approvers always can.", type: "toggle" },
      { key: "CallCentre:LeadsFromDate", label: "Only work web quotes from", help: "Quotes that arrived before this date are left out of the queue, the Quotes screen and the counts. They are not deleted. Blank shows everything.", type: "date" },
    ],
  },
  {
    title: "Lead types and age",
    description: "Who is served which leads first, and when a lead's age turns amber and red.",
    fields: [
      { key: "CallCentre:HighValueAmount", label: "High value from (£)", help: "An online quote at or above this. 0 turns it off.", type: "number" },
      { key: "CallCentre:LocalPostcode", label: "Local postcode", help: "Where Local is measured from.", savHidden: true },
      { key: "CallCentre:LocalRadiusMiles", label: "Local radius (miles)", type: "number", savHidden: true },
      { key: "CallCentre:SpecialistActiveMinutes", label: "Minutes a specialist counts as in", help: "Since their last saved call. With nobody in, their leads go to the pool.", type: "number" },
      { key: "CallCentre:AgeAmberHours", label: "Amber after (hours)", type: "number" },
      { key: "CallCentre:AgeRedHours", label: "Red after (hours)", type: "number" },
    ],
  },
  {
    title: "Pricing",
    description: "How the max is built. Percentages left at 0 leave the online quote as the max.",
    fields: [
      { key: "CallCentre:ExportTopUpPercent", label: "Export top-up (% of the Engines Worldwide engine offer)", type: "number" },
      { key: "CallCentre:CatTopUpPercent", label: "Cat top-up (% of the average cat price)", type: "number" },
      { key: "CallCentre:SalvageMinPercent", label: "Salvage floor (% of the valuation)", type: "number" },
      { key: "CallCentre:SalvageMaxPercent", label: "Salvage max (% of the valuation)", type: "number" },
      { key: "CallCentre:PartsMaxPercent", label: "Parts max (% of the initial price estimate)", type: "number" },
      { key: "CallCentre:PartsTopUpLowPercent", label: "Parts top-up, low (% of the initial price estimate)", help: "On scrap quotes. Where the agent starts the parts top-up.", type: "number" },
      { key: "CallCentre:PartsTopUpHighPercent", label: "Parts top-up, high (% of the initial price estimate)", help: "The most that goes into the max. 0 turns the parts top-up off.", type: "number" },
      { key: "CallCentre:OverMaxAllowancePercent", label: "Over-max allowance (%)", help: "How far agents can go above max without approval, in case the pricing is wrong.", type: "number" },
      { key: "CallCentre:NegotiationStep", label: "Negotiation step (£)", type: "number" },
      { key: "CallCentre:OfferValidityDays", label: "Days a price is held", help: "Something agents can tell the customer. Not enforced.", type: "number" },
    ],
  },
  {
    title: "Default targets",
    description: "For agents without their own targets below.",
    fields: [
      { key: "CallCentre:DefaultCallTarget", label: "Calls a day", type: "number" },
      { key: "CallCentre:DefaultBookingTarget", label: "Bookings a day", type: "number" },
    ],
  },
  {
    title: "If they say…",
    description: "The objection cards on the call screen. {max}, {step} and {validUntil} are filled in for each lead.",
    fields: [
      { key: "CallCentre:ObjectionBetterOfferTitle", label: "First card title" },
      { key: "CallCentre:ObjectionBetterOfferBody", label: "First card", type: "textarea" },
      { key: "CallCentre:ObjectionNotReadyTitle", label: "Second card title" },
      { key: "CallCentre:ObjectionNotReadyBody", label: "Second card", type: "textarea" },
    ],
  },
];

const ALL_TYPES = [CallLeadType.HighValue, CallLeadType.Salvage, CallLeadType.Local, CallLeadType.New];

function AgentRow({ user, agent, isSav }: { user: IWilmaUser; agent?: ICallAgent; isSav: boolean }) {
  const { upsertAgent } = useCallCentreSettings("CallCentreSettings");
  const { saveHandlers } = useDefaultCRUDHandlers("Agent");
  const [callTarget, setCallTarget] = useState(agent?.dailyCallTarget?.toString() ?? "");
  const [bookingTarget, setBookingTarget] = useState(agent?.dailyBookingTarget?.toString() ?? "");
  const [canApprove, setCanApprove] = useState(agent?.canApproveOffers ?? false);
  const [types, setTypes] = useState<CallLeadType[]>(agent?.leadTypes ?? []);
  const types_ = isSav ? ALL_TYPES.filter((x) => x !== CallLeadType.Local) : ALL_TYPES;

  const toggle = (type: CallLeadType) =>
    setTypes((current) => (current.includes(type) ? current.filter((x) => x !== type) : [...current, type]));

  const save = () =>
    upsertAgent.mutate(
      {
        userId: user.id,
        body: {
          dailyCallTarget: callTarget.trim() === "" ? null : Number(callTarget),
          dailyBookingTarget: bookingTarget.trim() === "" ? null : Number(bookingTarget),
          canApproveOffers: canApprove,
          leadTypes: types,
        },
      },
      {
        // Refusals come back 200 with success=false.
        onSuccess: (res) => (res.success ? saveHandlers.onSuccess() : saveHandlers.onError()),
        onError: () => saveHandlers.onError(),
      }
    );

  return (
    <tr className="align-top">
      <td className="px-3 py-3 text-sm font-medium text-gray-900">{user.fullname || user.email}</td>
      <td className="px-3 py-3">
        <input type="number" min={0} value={callTarget} onChange={(e) => setCallTarget(e.target.value)} placeholder="—" aria-label="Calls a day" className="w-20 rounded-md border-gray-300 text-sm" />
      </td>
      <td className="px-3 py-3">
        <input type="number" min={0} value={bookingTarget} onChange={(e) => setBookingTarget(e.target.value)} placeholder="—" aria-label="Bookings a day" className="w-20 rounded-md border-gray-300 text-sm" />
      </td>
      <td className="px-3 py-3">
        <input type="checkbox" checked={canApprove} onChange={(e) => setCanApprove(e.target.checked)} aria-label="Can approve offers" className="h-5 w-5 rounded border-gray-300 text-primary-600" />
      </td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-1.5">
          {types_.map((type) => {
            const position = types.indexOf(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggle(type)}
                aria-pressed={position >= 0}
                className={classNames(
                  "rounded-full border px-2.5 py-1 text-xs font-medium",
                  position >= 0 ? "border-slate-400 bg-slate-100 text-slate-800" : "border-gray-300 bg-white text-gray-600"
                )}
              >
                {position >= 0 ? `${position + 1}. ` : ""}{LEAD_TYPE_LABELS[type]}
              </button>
            );
          })}
        </div>
      </td>
      <td className="px-3 py-3 text-right">
        <button type="button" onClick={save} disabled={upsertAgent.isLoading} className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
          Save
        </button>
      </td>
    </tr>
  );
}

// The live queue's settings with labels instead of raw keys, and each agent's setup. Opening the page
// makes the API create any missing CallCentre: setting with its default.
export default function CallCentreSettings() {
  const { setInfo } = usePageNameContext();
  const queryClient = useQueryClient();
  const { customerId } = useAuth();
  const isSav = !!customerId && customerId === wilmaSAVCustomerId;
  const { provisioned, agents } = useCallCentreSettings("CallCentreSettings");
  const { settings, loading, update } = useSettings();
  const { wilmaUsers } = useWilmaUsers();
  const { saveHandlers } = useDefaultCRUDHandlers("Call Centre settings");
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    setInfo({ name: "Call Centre", desc: "Calling hours, pricing, targets and who is served what in the live queue" });
  }, [setInfo]);

  useEffect(() => {
    if (provisioned.data) queryClient.invalidateQueries(["settings"]);
  }, [provisioned.data, queryClient]);

  const byKey = useMemo(() => {
    const map = new Map<string, ISetting>();
    settings.forEach((s) => map.set(s.key, s));
    return map;
  }, [settings]);

  useEffect(() => {
    const next: Record<string, string> = {};
    GROUPS.forEach((g) => g.fields.forEach((f) => (next[f.key] = byKey.get(f.key)?.value ?? provisioned.data?.data?.[f.key] ?? "")));
    setValues(next);
  }, [byKey, provisioned.data]);

  const saveGroup = async (fields: Field[]) => {
    const changed = fields
      .map((f) => ({ setting: byKey.get(f.key), value: values[f.key] ?? "" }))
      .filter((x) => x.setting && x.setting.value !== x.value);

    try {
      for (const { setting, value } of changed) await update.mutateAsync({ ...setting!, value });
      saveHandlers.onSuccess();
    } catch {
      saveHandlers.onError();
    }
  };

  const agentsByUser = useMemo(() => {
    const map = new Map<string, ICallAgent>();
    (agents.data?.data ?? []).forEach((a) => map.set(a.userId, a));
    return map;
  }, [agents.data]);

  if (loading || provisioned.isLoading) return <LoadingWheel />;

  if (provisioned.data && !provisioned.data.success) {
    return <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{provisioned.data.message}</p>;
  }

  return (
    <div className="space-y-6">
      {GROUPS.map((group) => (
        <section key={group.title} className="rounded-lg bg-white p-5 shadow">
          <h2 className="text-base font-semibold text-gray-900">{group.title}</h2>
          <p className="mt-1 text-sm text-gray-600">{group.description}</p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {group.fields.filter((f) => !(isSav && f.savHidden)).map((field) => (
              <label key={field.key} className={classNames("flex flex-col gap-1 text-sm font-medium text-gray-700", field.type === "textarea" && "md:col-span-2")}>
                {field.label}
                {field.type === "textarea" ? (
                  <textarea rows={2} value={values[field.key] ?? ""} onChange={(e) => setValues({ ...values, [field.key]: e.target.value })} className="rounded-md border-gray-300 text-sm font-normal" />
                ) : field.type === "toggle" ? (
                  <input
                    type="checkbox"
                    checked={values[field.key] === "true"}
                    onChange={(e) => setValues({ ...values, [field.key]: e.target.checked ? "true" : "false" })}
                    className="h-5 w-5 rounded border-gray-300 text-primary-600"
                  />
                ) : (
                  <input
                    type={field.type ?? "text"}
                    value={values[field.key] ?? ""}
                    onChange={(e) => setValues({ ...values, [field.key]: e.target.value })}
                    className="rounded-md border-gray-300 text-sm font-normal"
                  />
                )}
                {field.help && <span className="text-xs font-normal text-gray-500">{field.help}</span>}
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={() => saveGroup(group.fields)} disabled={update.isLoading} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
              Save
            </button>
          </div>
        </section>
      ))}

      <section className="rounded-lg bg-white p-5 shadow">
        <h2 className="text-base font-semibold text-gray-900">Agents</h2>
        <p className="mt-1 text-sm text-gray-600">
          Targets left blank use the defaults. Lead types are served before the pool, in the order you pick them.
          Agents who can approve offers see the approvals and can open any lead in the queue.
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Calls a day</th>
                <th className="px-3 py-2">Bookings a day</th>
                <th className="px-3 py-2">Can approve</th>
                <th className="px-3 py-2">Served first</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {agents.isLoading || wilmaUsers.isLoading ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : (
                (wilmaUsers.data?.data ?? []).map((user) => (
                  <AgentRow key={`${user.id}-${agentsByUser.get(user.id)?.id ?? "new"}`} user={user} agent={agentsByUser.get(user.id)} isSav={isSav} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
